import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Menu,
  MenuList,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Snackbar,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  ChatBubbleOutline as PromptIcon,
  Login as LoginIcon,
  MoreVert as MoreVertIcon,
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import companyService, { Company, CompanyListResponse } from '../../services/company.service';
import { MOBLA_FE_URL } from '../../config/config';

const CompaniesList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // State variables
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('active');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);

  // Row "more actions" menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuCompany, setMenuCompany] = useState<Company | null>(null);

  // Change password dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState<boolean>(false);
  const [companyForPassword, setCompanyForPassword] = useState<Company | null>(null);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [passwordSaving, setPasswordSaving] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch companies on component mount and when filters/pagination changes
  useEffect(() => {
    fetchCompanies();
  }, [page, rowsPerPage, selectedStatus, searchQuery]);

  // Function to fetch companies with filters and pagination
  const fetchCompanies = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await companyService.getCompanies({
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
        status: selectedStatus || undefined,
        search: searchQuery || undefined,
      });

      setCompanies(response.data);
      setTotalRows(response.pagination.total);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch companies');
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when search changes
  };

  // Handle status filter change
  const handleStatusChange = (event: any) => {
    setSelectedStatus(event.target.value as string);
    setPage(0); // Reset to first page when filter changes
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedStatus('');
    setPage(0);
  };

  // Row actions menu handlers
  const openRowMenu = (event: React.MouseEvent<HTMLElement>, company: Company) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuCompany(company);
  };

  const closeRowMenu = () => {
    setMenuAnchorEl(null);
    setMenuCompany(null);
  };

  // Run a menu action for the company whose menu is open, then close the menu
  const handleMenuAction = (action: (company: Company) => void) => () => {
    if (menuCompany) action(menuCompany);
    closeRowMenu();
  };

  // Dialog handlers
  const openDeleteDialog = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
  };

  // Delete company
  const handleDeleteCompany = async () => {
    if (!companyToDelete?._id) return;

    try {
      await companyService.deleteCompany(companyToDelete._id);
      fetchCompanies(); // Refresh the list
      closeDeleteDialog();
    } catch (err) {
      console.error('Error deleting company:', err);
      // You could set an error state here to show an error message
    }
  };

  // Change password dialog handlers
  const openPasswordDialog = (company: Company) => {
    setCompanyForPassword(company);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setPasswordError(null);
    setPasswordDialogOpen(true);
  };

  const closePasswordDialog = () => {
    if (passwordSaving) return;
    setPasswordDialogOpen(false);
    setCompanyForPassword(null);
  };

  // Set a new login password for the company's account
  const handleChangePassword = async () => {
    if (!companyForPassword?._id) return;

    if (newPassword.trim().length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordSaving(true);
    setPasswordError(null);

    try {
      await companyService.updateCompanyPassword(companyForPassword._id, newPassword.trim());
      setPasswordDialogOpen(false);
      setCompanyForPassword(null);
      setSuccessMessage('Password updated successfully');
    } catch (err: any) {
      setPasswordError(err?.response?.data?.message || 'Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Open the company's account on mobla.ai in a new tab (auto-login)
  const handleLoginToMobla = (company: Company) => {
    if (!company?._id) return;
    const url = `${MOBLA_FE_URL}auto-login?companyId=${company._id}`;
    window.open(url, '_blank');
  };

  // Render status chip based on status value
  const renderStatusChip = (status: string) => {
    let color: 'success' | 'error' | 'warning' | 'default' = 'default';

    switch (status) {
      case 'active':
        color = 'success';
        break;
      case 'inactive':
        color = 'error';
        break;
      case 'pending':
        color = 'warning';
        break;
      default:
        color = 'default';
    }

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={color}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            }
          }}
        >
          Companies
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/companies/add')}
          sx={{
            px: 3,
            boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          Add Company
        </Button>
      </Box>

      {/* Filters */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            id="status-filter"
            value={selectedStatus}
            onChange={handleStatusChange}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={resetFilters}
        >
          Reset
        </Button>
      </Paper>

      {/* Companies Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Registed On</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No companies found
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company: any) => (
                  <TableRow key={company._id} hover>
                    <TableCell>
                      <Typography
                        sx={{
                          color: theme.palette.primary.main,
                          cursor: 'pointer',
                          fontWeight: 500,
                          '&:hover': {
                            textDecoration: 'underline',
                          }
                        }}
                        onClick={() => navigate(`/companies/${company._id}`)}
                      >
                        {company.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{company.email_id}</TableCell>
                    <TableCell>{company.contact_no}</TableCell>
                    <TableCell>{new Date(company?.createdAt).toLocaleDateString() || '-'}</TableCell>
                    <TableCell>{renderStatusChip(company.status)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Login to mobla.ai">
                        <IconButton
                          color="success"
                          onClick={() => handleLoginToMobla(company)}
                          sx={{ mr: 0.5 }}
                        >
                          <LoginIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Prompts">
                        <IconButton
                          color="info"
                          onClick={() => navigate(`/companies/${company._id}`, { state: { isPrompts: true } })}
                          sx={{ mr: 0.5 }}
                        >
                          <PromptIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More actions">
                        <IconButton
                          onClick={(event) => openRowMenu(event, company)}
                          aria-label="More actions"
                          aria-haspopup="true"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Row Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        <MenuList dense>
          <MenuItem onClick={handleMenuAction((company) => navigate(`/companies/${company._id}`, { state: { isPrompts: true } }))}>
            <ListItemIcon>
              <PromptIcon fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>Prompts</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleMenuAction((company) => navigate(`/companies/${company._id}`))}>
            <ListItemIcon>
              <InfoIcon fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleMenuAction((company) => navigate(`/companies/edit/${company._id}`))}>
            <ListItemIcon>
              <EditIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleMenuAction(openPasswordDialog)}>
            <ListItemIcon>
              <LockResetIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Change Password</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleMenuAction(openDeleteDialog)} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the company "{companyToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteCompany} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={closePasswordDialog}
        fullWidth
        maxWidth="xs"
        aria-labelledby="password-dialog-title"
      >
        <DialogTitle id="password-dialog-title">Change Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set a new login password for "{companyForPassword?.name}". The company will use it with{' '}
            {companyForPassword?.email_id} to sign in.
          </DialogContentText>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            autoFocus
            size="small"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            size="small"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            helperText="Minimum 6 characters"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog} disabled={passwordSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={passwordSaving || !newPassword || !confirmPassword}
            startIcon={passwordSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {passwordSaving ? 'Saving...' : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompaniesList;